/**
 * Recommendation funnel + cards quality-signal tracking
 * (H-2026-04-25-002 — artemis contract).
 *
 * @packageDocumentation
 * @public
 *
 * CANONICAL copy (hoisted 2026-07-07 from apps/creator-hub; mobile imports
 * this module; apps/shopify-theme/assets/tracking-funnel.js keeps a
 * classic-script window.MMSFunnel mirror because Liquid loads no ESM — keep
 * its wire format aligned with this file).
 *
 * Two event families share this one transport (hosts per ADR-006):
 *   1. recommendation funnel: impression / click / cart / purchase
 *      → POST admin-api.mamamiya.store/api/recommendation/track/*
 *      Used to populate recommendation_funnel (mms-system) so Hermes
 *      dashboards stop returning zeros.
 *   2. cards quality signals: adopt / discard
 *      → POST creator-api.mamamiya.store/api/customer/cards/:id/{adopt,discard}
 *      Used to backfill prompt_cards.adopt_count / discard_count so
 *      quality_score = adopt / (adopt + discard) is computable.
 *
 * Errors are silent on purpose: an analytics POST that fails MUST NOT
 * disrupt the user's flow. fetch keepalive: true so the request
 * survives a navigation away (Beacon API would also work but isn't
 * universally supported).
 *
 * Endpoints and request shape are authoritative per
 * The shared funnel event contract is implemented in this module.
 * sections A.1 and A.2. Do not change without coordinating with @artemis.
 */

// rec-api worker is a ghost post-ADR-002 (H-2026-05-15-008 deferred); the
// funnel endpoints land on admin-api's capability registry instead.
// Cards adopt/discard live on mms-creator (creator-api) under the
// /api/customer/cards/:id/{adopt,discard} routes.
const REC_API = 'https://admin-api.mamamiya.store'
const CARDS_API = 'https://creator-api.mamamiya.store'

function authHeader() {
  try {
    const t =
      typeof localStorage !== 'undefined' ? localStorage.getItem('mms_customer_token') : null
    return t ? { Authorization: `Bearer ${t}` } : {}
  } catch {
    return {}
  }
}

function silentPostAuthed(url, body) {
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: body ? JSON.stringify(body) : undefined,
      keepalive: true,
    }).catch(() => {})
  } catch {
    // ignore — analytics must never disrupt UX
  }
}

/**
 * Generate a unique recommendationId for an impression. The same id MUST
 * be reused for follow-up click / cart / purchase events on the same card
 * so the backend can JOIN them into a funnel row.
 *
 * @returns {string}
 */
export function mintRecId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'rec_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10)
}

/**
 * Coarse device class for funnel rows.
 * @returns {'mobile' | 'desktop'} `'mobile'` when the UA carries a Mobi token
 */
function detectDeviceType() {
  if (typeof navigator === 'undefined') return 'desktop'
  return /Mobi/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
}

function silentPost(url, body) {
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {})
  } catch {
    // ignore — analytics must never disrupt UX
  }
}

/**
 * Send an impression event. Returns the recommendationId (newly minted
 * if none was provided) so the caller can stash it on the DOM
 * (e.g., element.dataset.recId) for later click/cart/purchase events.
 *
 * @param {Object} input
 * @param {number|string} input.productId
 * @param {string} input.scenario
 * @param {number} input.position
 * @param {string} input.pageType
 * @param {string|null} [input.userId]
 * @param {string} [input.sessionId]
 * @param {string} [input.recommendationId]
 * @returns {string} recommendationId
 */
export function trackRecImpression({
  productId,
  scenario,
  position,
  pageType,
  userId = null,
  sessionId,
  recommendationId,
}) {
  const id = recommendationId || mintRecId()
  silentPost(`${REC_API}/api/recommendation/track/impression`, {
    recommendationId: id,
    scenario,
    productId,
    userId,
    sessionId,
    position,
    pageType,
    deviceType: detectDeviceType(),
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
  })
  return id
}

/**
 * Funnel click event for a previously-impressed card.
 * @public
 * @param {string} recommendationId - The id minted at impression time
 * @returns {void}
 */
export function trackRecClick(recommendationId) {
  if (!recommendationId) return
  silentPost(`${REC_API}/api/recommendation/track/click`, { recommendationId })
}

/**
 * Funnel add-to-cart event for a previously-impressed card.
 * @public
 * @param {string} recommendationId - The id minted at impression time
 * @returns {void}
 */
export function trackRecCart(recommendationId) {
  if (!recommendationId) return
  silentPost(`${REC_API}/api/recommendation/track/cart`, { recommendationId })
}

/**
 * Funnel purchase event closing the loop on an impressed card.
 * @public
 * @param {string} recommendationId - The id minted at impression time
 * @param {number} revenue - Order revenue attributed to the recommendation
 * @returns {void}
 */
export function trackRecPurchase(recommendationId, revenue) {
  if (!recommendationId) return
  silentPost(`${REC_API}/api/recommendation/track/purchase`, { recommendationId, revenue })
}

/**
 * Cards quality signals.
 *
 * Endpoint migrated to creator-api `/api/customer/cards/:id/{adopt,discard}`
 * per bao backend dispatch (2026-05-23). Adopt is auto-written server-side
 * on successful generate, so the client call is a redundant double-write
 * (dedupe-safe); discard is the real user signal.
 *
 * NOTE — id semantics: backend route is `cardId` (prompt_cards.id). Current
 * callers in dialog-shell.js still pass `result.generation_id`
 * (prompt_records.id). Verify with @zeus whether the backend treats this
 * id as cardId or has a generationId-compatible lookup. If not compatible,
 * a follow-up commit will thread cardId through creation.result.
 *
 * @public
 * @param {string} cardId - prompt_cards.id (TEXT, ULID-style)
 * @returns {void}
 */
export function trackCardAdopt(cardId) {
  // cards.id is TEXT (ULID-style) — charset guard, not numeric.
  if (!cardId || !/^[\w-]+$/.test(String(cardId))) return
  silentPostAuthed(`${CARDS_API}/api/customer/cards/${encodeURIComponent(cardId)}/adopt`)
}

/**
 * Card discard quality signal — the real negative user signal (see the
 * adopt note above).
 * @public
 * @param {string} cardId - prompt_cards.id (TEXT, ULID-style)
 * @param {'effect_mismatch'|'subject_changed'|'wrong_style'|null} [reason] - Structured discard reason
 * @returns {void}
 */
export function trackCardDiscard(cardId, reason = null) {
  if (!cardId || !/^[\w-]+$/.test(String(cardId))) return
  silentPostAuthed(
    `${CARDS_API}/api/customer/cards/${encodeURIComponent(cardId)}/discard`,
    reason ? { reason } : null
  )
}

// ---- v0.7 funnel events (creator-dialog-v07) ----------------------------
//
// Event names and payloads follow the shared funnel contract above.
// Backend endpoint `${REC_API}/api/funnel/event` is owned by H-25-002
// (artemis recommendation lane). Until that endpoint ships in production,
// FUNNEL_EVENTS_ENABLED gates the actual silentPost so no orphan POSTs fire.
//
// Override at runtime via:
//   localStorage.setItem('mms_funnel_events', '1' | '0')
//
// Default OFF to avoid burning credits on a 404 when the endpoint isn't up
// yet. Flip ON in dev / when artemis confirms backend ready.

function funnelEventsEnabled() {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem('mms_funnel_events') : null
    return v === '1'
  } catch {
    return false
  }
}

function postFunnelEvent(payload) {
  if (!funnelEventsEnabled()) return
  let sessionId = null
  try {
    sessionId = typeof localStorage !== 'undefined' ? localStorage.getItem('mms_session_id') : null
  } catch {
    sessionId = null
  }
  silentPost(`${REC_API}/api/funnel/event`, { ...payload, session_id: sessionId })
}

/**
 * F-class client tool was invoked (handler ran successfully).
 * @param {string} intentId        e.g. 'F1'
 * @param {string} executionMode   'client_canvas' | 'client_svg' | 'client_text' | 'cached_template' | 'client_pixel'
 */
export function trackClientIntentUsed(intentId, executionMode) {
  if (!intentId || !executionMode) return
  postFunnelEvent({
    type: 'client_intent_used',
    intent_id: intentId,
    execution_mode: executionMode,
  })
}

/**
 * User toggled a bundle chip on/off.
 * @param {number} imageId
 * @param {number} cardId
 * @param {0|1}    isOn   target state after the toggle
 */
export function trackBundleCardToggle(imageId, cardId, isOn) {
  if (!imageId || !cardId) return
  postFunnelEvent({
    type: 'bundle_card_toggled',
    image_id: imageId,
    card_id: cardId,
    is_on: isOn,
  })
}

/**
 * User inline-edited a bundle chip description (fork-on-edit POST committed).
 * @param {number} imageId
 * @param {number} newCardId      id of the user_edit row just inserted
 * @param {number} parentCardId   id of the original ai_bundle row (lineage)
 */
export function trackBundleCardEdit(imageId, newCardId, parentCardId) {
  if (!imageId || !newCardId) return
  postFunnelEvent({
    type: 'bundle_card_edited',
    image_id: imageId,
    card_id: newCardId,
    parent_card_id: parentCardId || null,
  })
}

/**
 * IntersectionObserver-driven impression sender. Wires every element
 * matching `selector` so it fires impression exactly once when ≥50%
 * visible. Each element must carry `data-product-id` and
 * `data-position`. The minted recommendationId is written back to
 * `data-rec-id` so click handlers can read it.
 *
 * Returns the observer so the caller can disconnect on unmount.
 *
 * @param {Object} cfg
 * @param {string} cfg.selector - e.g. '.rec-card'
 * @param {string} cfg.scenario - e.g. 'designer_style_picks'
 * @param {string} cfg.pageType - e.g. 'designer'
 * @param {() => string|undefined} cfg.getSessionId
 * @param {() => string|null} [cfg.getUserId]
 * @param {Element|Document} [cfg.root=document]
 * @returns {IntersectionObserver}
 */
export function observeImpressions(cfg) {
  const root = cfg.root ?? document
  const getSessionId = cfg.getSessionId
  const getUserId = cfg.getUserId ?? (() => null)

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return
        const el = /** @type {HTMLElement} */ (e.target)
        if (el.dataset.recId) return
        const productId = Number(el.dataset.productId)
        if (!productId) return
        const position = Number(el.dataset.position ?? 0)
        const id = trackRecImpression({
          productId,
          scenario: cfg.scenario,
          position,
          pageType: cfg.pageType,
          sessionId: getSessionId(),
          userId: getUserId() || null,
        })
        el.dataset.recId = id
        io.unobserve(el)
      })
    },
    { threshold: 0.5 }
  )

  root.querySelectorAll(cfg.selector).forEach((el) => io.observe(el))
  return io
}

/**
 * Read-or-mint the browser session id used to join funnel events
 * (`mms_session_id` in localStorage) — every surface joins on the same key.
 *
 * @public
 * @param {string} [prefix] - id prefix when minting, default `'web_'`
 * @returns {string|undefined} the session id, or undefined outside a browser
 */
export function getOrCreateSessionId(prefix = 'web_') {
  if (typeof localStorage === 'undefined') return undefined
  let id = localStorage.getItem('mms_session_id')
  if (!id) {
    id = prefix + Date.now() + '_' + Math.random().toString(36).slice(2, 10)
    try {
      localStorage.setItem('mms_session_id', id)
    } catch {
      // ignore quota / privacy errors
    }
  }
  return id
}
